import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';

import {
  FormField,
  FormItem,
  FormControl,
} from '@components/ui/form';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { Label } from '@components/ui/label';

import { cn, isLollySkin } from '@utils/index';

import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import { FormMessageSpan } from '@components/ui/form';
import { AI_HOOKS_IDS } from '@features/onboarding/constants';
import { useAIStore } from 'src/store/ai';

interface CoverageStartDateStepProps {
  question: HealthQuestion;
  form: UseFormReturn<HealthQuestionsSchema>;
  errorMessage?: string | null;
  setStepErrorMessage?: (msg: string | null) => void;
}

interface HealthOption {
  value: string;
  label: string;
}

function isHealthOption(option: string | HealthOption): option is HealthOption {
  return !!(option && typeof option === 'object' && !Array.isArray(option) && 'value' in option && 'label' in option);
}

export const CoverageStartDateStep: React.FC<CoverageStartDateStepProps> = ({
  question,
  form,
  errorMessage,
  setStepErrorMessage,
}) => {
  const { setCurrentPage } = useAIStore();

  // Initialize data from onboarding data or applications[0] if available
  useEffect(() => {
    setTimeout(() => {
      setCurrentPage(AI_HOOKS_IDS.EFFECTIVE_DATE);
    }, 200);

  }, []);
  // Flatten options to handle both single items and arrays
  const flattenedOptions = React.useMemo(() => {
    if (!question.options) return [];

    const result: (string | HealthOption)[] = [];

    question.options.forEach(option => {
      if (Array.isArray(option)) {
        result.push(...option);
      } else {
        result.push(option);
      }
    });

    return result;
  }, [question.options]);

  // healthQuestions.coverageStartDate.effectiveDate

  return (
    <>
      <h3 className="text-lg md:text-xl font-bold mb-4 m-auto w-fit">
        Please select one of the following effective dates:
      </h3>

      <FormField
        control={form.control}
        name="healthQuestions.coverageStartDate.effectiveDate"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RadioGroup
                onValueChange={(val) => {
                  field.onChange(val);
                  if (setStepErrorMessage) {
                    setStepErrorMessage(null);
                  }
                }}
                value={field.value || ''}
                className="flex flex-wrap gap-x-4 gap-y-2 flex-col justify-center items-center max-w-md mx-auto"
              >
                {flattenedOptions
                  .filter(option => typeof option === 'string' || isHealthOption(option))
                  .map((option, index) => {
                    const inputId = `${question.id}-${index}`;
                    const value = typeof option === 'string' ? option : option.value;
                    const label = typeof option === 'string' ? option : option.label;

                    return (
                      <Label
                        key={index}
                        htmlFor={inputId}
                        className={cn(
                          'shadow',
                          'flex justify-between space-x-5 cursor-pointer w-fit items-center rounded-2xl',
                          'w-full lg:w-84 h-20 pl-6 pr-5',
                          'transition-colors',
                          isLollySkin() && 'bg-white',
                          field.value === value ? 'radio-wrapper-checked' : ''
                        )}
                      >
                        <span
                          className={cn(
                            'font-bold text-2xl',
                            field.value === value
                              ? 'text-black'
                              : 'text-gray-500'
                          )}
                        >
                          {label}
                        </span>
                        <RadioGroupItem
                          value={value}
                          id={inputId}
                          className="pointer-events-none"
                        />
                      </Label>
                    );
                  })}
              </RadioGroup>
            </FormControl>
            {form.formState.errors.healthQuestions?.coverageStartDate
              ?.effectiveDate && (
              <p className="text-primary mt-2">
                {
                  form.formState.errors.healthQuestions.coverageStartDate
                    .effectiveDate.message
                }
              </p>
            )}
          </FormItem>
        )}
      />
      <p className="mt-6 max-w-md mx-auto text-center text-sm text-gray-500">
        Please be advised that if you enroll after the 18th of the month,
        delivery of your member ID cards and the availability of your information
        in the third-party administrator&apos;s system may be delayed.
      </p>
      {errorMessage && (
        <FormMessageSpan
          className="mt-2 block text-md text-center w-full"
          text={errorMessage}
        />
      )}
    </>
  );
};
